import api from './api';
import type { EventCustomDomain } from './customDomainService';

export type ParishLandingTemplate = 'events_bio' | 'comedy_club';

export type ParishLandingEventsSortMode = 'createdAt' | 'startDate' | 'custom';

export interface ParishLandingEvent {
  id: string;
  title: string;
  slug: string;
  status: string;
  coverImage?: string;
  startDate?: string;
  endDate?: string;
  location?: string;
}

export interface ParishLandingLink {
  label: string;
  url: string;
  order: number;
}

export interface ParishLandingData {
  id: string;
  parishId: string;
  parishSlug?: string;
  parishName?: string;
  template: ParishLandingTemplate;
  published: boolean;
  profile: {
    title: string;
    bio: string;
    avatarUrl: string;
    coverUrl: string;
  };
  theme: {
    primaryColor: string;
    secondaryColor: string;
    background: string;
  };
  eventIds: string[];
  /** Só relevante no perfil Zuadaria: ordem de criação, data do show ou personalizada. */
  eventsSortMode?: ParishLandingEventsSortMode;
  links: ParishLandingLink[];
  events: ParishLandingEvent[];
  createdAt?: string;
  updatedAt?: string;
}

export interface PublicParishLandingPayload {
  targetType: 'landing';
  domain?: string;
  parishId: string;
  parishSlug: string;
  parishName: string;
  parishLogo?: string | null;
  /** White-label; omitido = platform */
  brandKey?: 'platform' | 'zuadaria';
  landing: ParishLandingData;
  paths: {
    home: string;
    preview: string;
  };
}

export async function getParishLanding(): Promise<ParishLandingData> {
  const response = await api.get('/parishes/landing');
  return response.data.data;
}

export async function saveParishLanding(payload: Partial<ParishLandingData>): Promise<ParishLandingData> {
  const response = await api.put('/parishes/landing', payload);
  return response.data.data;
}

export async function getParishLandingCustomDomain(): Promise<EventCustomDomain | null> {
  const response = await api.get('/parishes/landing/custom-domain');
  return response.data?.data ?? null;
}

export async function saveParishLandingCustomDomain(domain: string): Promise<EventCustomDomain> {
  const response = await api.put('/parishes/landing/custom-domain', { domain });
  return response.data.data;
}

export async function verifyParishLandingCustomDomain(): Promise<EventCustomDomain> {
  const response = await api.post('/parishes/landing/custom-domain/verify');
  return response.data.data;
}

export async function disableParishLandingCustomDomain(): Promise<EventCustomDomain> {
  const response = await api.delete('/parishes/landing/custom-domain');
  return response.data.data;
}

export async function getPublicLandingByBrand(
  brandKey: string
): Promise<PublicParishLandingPayload | null> {
  try {
    const response = await api.get(`/public/landing-by-brand/${encodeURIComponent(brandKey)}`);
    return response.data?.data ?? null;
  } catch {
    return null;
  }
}

export async function getPublicParishLanding(
  parishIdOrSlug: string
): Promise<PublicParishLandingPayload | null> {
  try {
    const response = await api.get(`/public/landing/${encodeURIComponent(parishIdOrSlug)}`);
    return response.data?.data ?? null;
  } catch (err: any) {
    const exclusiveDomain = err?.response?.data?.exclusiveDomain as string | undefined;
    if (exclusiveDomain) {
      const error = new Error('EXCLUSIVE_DOMAIN') as Error & { exclusiveDomain?: string };
      error.exclusiveDomain = exclusiveDomain;
      throw error;
    }
    return null;
  }
}

export type PublicLandingFetchResult =
  | { ok: true; data: PublicParishLandingPayload }
  | { ok: false; error: string; exclusiveDomain?: string };
